import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Crown,
  Shield,
  Gem,
  Infinity,
  Zap,
  Star,
  Users,
  Target,
  Brain,
  Globe,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const ExpertiseSection = () => {
  const { t, isRTL } = useLanguage();

  return (
    <section className="py-32 lg:py-40 bg-slate-900 text-white relative overflow-hidden">
      {/* Premium Dark Background with Glass Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 -right-32 w-80 h-80 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '-3s' }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_20%,_rgba(15,23,42,0.3)_100%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header mit Premium Badge */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 glass-card bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-xl mb-12">
            <Star className="w-5 h-5 text-orange-400 mr-3" />
            <span className="text-sm font-medium text-slate-200">
              {t('expertise.badgeElite')}
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-light text-white mb-8">
            {t('expertise.title')}{' '}
            <span className="gradient-text font-medium">
              {t('expertise.titleHighlight')}
            </span>
          </h2>

          <div className="max-w-4xl mx-auto space-y-6 text-lg text-slate-300 leading-relaxed font-light">
            <p>{t('expertise.description')}</p>

            <p className="text-xl font-medium text-white">
              {t('expertise.subDescription')}
            </p>
          </div>
        </div>

        {/* Quatre Services */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
          {t('expertise.services').map((service, index) => (
            <div
              key={service.id}
              className="group relative overflow-hidden rounded-3xl glass-card bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/15 transition-all duration-700 hover:scale-105"
            >
              {/* Background Image avec Dark Overlay */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.background}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />

                {/* Premium Gradient Overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-30 group-hover:opacity-40 transition-opacity duration-500`}
                />

                {/* Glass Icon Container mit Premium Styling */}
                <div className="absolute bottom-6 left-6">
                  <div className="glass-card bg-white/20 backdrop-blur-xl border border-white/30 text-white mb-3 p-4 rounded-2xl w-fit transition-all duration-500 group-hover:bg-white/30 shadow-2xl">
                    {service.iconName === 'Zap' && <Zap className="w-8 h-8" />}
                    {service.iconName === 'Users' && (
                      <Users className="w-8 h-8" />
                    )}
                    {service.iconName === 'Target' && (
                      <Target className="w-8 h-8" />
                    )}
                    {service.iconName === 'Star' && <Star className="w-8 h-8" />}
                  </div>
                  <h3 className="text-2xl font-light text-white mb-1 transition-all duration-300 group-hover:text-orange-100 drop-shadow-lg">
                    {service.title}
                  </h3>
                  <p className="text-orange-300 font-medium transition-all duration-300 group-hover:text-orange-200 drop-shadow-lg">
                    {service.subtitle}
                  </p>
                </div>

                {/* Cinematic Light Effect */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>

              {/* Content avec Dark Theme */}
              <div className="p-8 bg-gradient-to-b from-slate-800/80 to-slate-900/90 backdrop-blur-sm transition-all duration-500 group-hover:from-slate-800/90 group-hover:to-slate-900/95">
                <p className="text-slate-300 leading-relaxed mb-6 transition-colors duration-300 group-hover:text-slate-200">
                  {service.description}
                </p>

                {/* Features mit Dark Styling */}
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-slate-400 group-hover:text-slate-300 transition-colors duration-300"
                    >
                      <div
                        className={`w-1.5 h-1.5 bg-gradient-to-r ${service.gradient} rounded-full mr-3 flex-shrink-0 transition-all duration-300 group-hover:w-2 group-hover:h-2`}
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Premium Glass CTA */}
                <Link
                  to={service.href}
                  className="group/btn inline-flex items-center px-4 py-3 glass-card bg-white/10 backdrop-blur-xl border border-white/20 text-white rounded-full font-medium transition-all duration-500 hover:bg-white/20 hover:shadow-glow hover:scale-105"
                >
                  <span className="mr-3 text-sm">{service.cta}</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-2" />
                </Link>
              </div>

              {/* Premium Floating Element */}
              <div className="absolute top-4 right-4 glass-card bg-white/5 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center border border-white/10 opacity-0 group-hover:opacity-100 transition-all duration-700">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-orange-400 rounded-full animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>

        {/* GLOBRIDGE DNA - Dark Theme Premium */}
        <div className="text-center max-w-6xl mx-auto mt-24">
          <div className="inline-flex items-center px-6 py-3 glass-card bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-xl mb-12">
            <Crown className="w-5 h-5 text-orange-400 mr-3" />
            <span className="text-sm font-medium text-slate-200">
              {t('expertise.dna.badge')}
            </span>
          </div>

          <h3 className="text-3xl lg:text-4xl font-light text-white mb-8">
            {t('expertise.dna.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('expertise.dna.titleHighlight')}
            </span>{' '}
            {t('expertise.dna.titleAfterHighlight')}
          </h3>

          <p className="text-xl text-slate-300 leading-relaxed font-light mb-16 max-w-4xl mx-auto">
            {t('expertise.dna.description')}
          </p>

          {/* Innovation Highlights - Dark Theme */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-20">
            {/* Systemische Tiefe */}
            <div className="group text-center">
              <div className="glass-card bg-white/5 backdrop-blur-xl border border-purple-300/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 h-full hover:bg-white/10">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {t('expertise.dna.systemicDepth.title')}
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {t('expertise.dna.systemicDepth.line1')}
                  <br />
                  {t('expertise.dna.systemicDepth.line2')}
                </p>
                <div className="px-3 py-1 bg-purple-500/20 backdrop-blur-sm rounded-full text-xs text-purple-300 font-medium border border-purple-400/30">
                  {t('expertise.dna.systemicDepth.chip')}
                </div>
                <div className="mt-3 h-0.5 w-full bg-gradient-to-r from-purple-600 to-blue-600 rounded-full opacity-40 group-hover:opacity-80 transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Kulturelle Intelligenz */}
            <div className="group text-center">
              <div className="glass-card bg-white/5 backdrop-blur-xl border border-emerald-300/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 h-full hover:bg-white/10">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {t('expertise.dna.culturalIntelligence.title')}
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {t('expertise.dna.culturalIntelligence.line1')}
                  <br />
                  {t('expertise.dna.culturalIntelligence.line2')}
                </p>
                <div className="px-3 py-1 bg-emerald-500/20 backdrop-blur-sm rounded-full text-xs text-emerald-300 font-medium border border-emerald-400/30">
                  {t('expertise.dna.culturalIntelligence.chip')}
                </div>
                <div className="mt-3 h-0.5 w-full bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full opacity-40 group-hover:opacity-80 transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Menschliche Resonanz */}
            <div className="group text-center">
              <div className="glass-card bg-white/5 backdrop-blur-xl border border-orange-300/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 h-full hover:bg-white/10">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {t('expertise.dna.humanResonance.title')}
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {t('expertise.dna.humanResonance.line1')}
                  <br />
                  {t('expertise.dna.humanResonance.line2')}
                </p>
                <div className="px-3 py-1 bg-orange-500/20 backdrop-blur-sm rounded-full text-xs text-orange-300 font-medium border border-orange-400/30">
                  {t('expertise.dna.humanResonance.chip')}
                </div>
                <div className="mt-3 h-0.5 w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full opacity-40 group-hover:opacity-80 transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Messbare Transformation */}
            <div className="group text-center">
              <div className="glass-card bg-white/5 backdrop-blur-xl border border-violet-300/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 h-full hover:bg-white/10">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {t('expertise.dna.measurableImpact.title')}
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {t('expertise.dna.measurableImpact.line1')}
                  <br />
                  {t('expertise.dna.measurableImpact.line2')}
                </p>
                <div className="px-3 py-1 bg-violet-500/20 backdrop-blur-sm rounded-full text-xs text-violet-300 font-medium border border-violet-400/30">
                  {t('expertise.dna.measurableImpact.chip')}
                </div>
                <div className="mt-3 h-0.5 w-full bg-gradient-to-r from-violet-500 to-purple-600 rounded-full opacity-40 group-hover:opacity-80 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Single Premium CTA - Reduced to Elite Level */}
        <div className="text-center mt-20 mb-16">
          <p className="text-xl font-light text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('expertise.cta.questionBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('expertise.cta.highlight')}
            </span>
            ?
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-10 py-5 glass-card bg-gradient-to-r from-purple-600/80 to-orange-500/80 backdrop-blur-xl border border-white/20 text-white rounded-full font-medium text-lg transition-all duration-500 hover:shadow-glow hover:scale-105 hover:bg-gradient-to-r hover:from-purple-600/90 hover:to-orange-500/90"
          >
            <span className="mr-4">{t('expertise.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};
